import { useEffect } from 'react';

/**
 * 修复移动端软键盘弹起后页面被放大且无法自动恢复的问题。
 * 主要依赖：输入框 font-size >= 16px（已在 CSS 中设置）。
 * 本 hook 作为兜底，在键盘收起 / 屏幕旋转 / resize 时强制重置 viewport scale。
 */
export function useViewportReset() {
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    const viewport = document.querySelector('meta[name=viewport]');
    if (!viewport) return;

    const originalContent = viewport.getAttribute('content') || 'width=device-width, initial-scale=1.0';

    function resetZoom() {
      // 临时把 maximum-scale 锁到 1 再恢复，强制浏览器回到 initial-scale
      // 前面已判定 viewport 存在，这里用 ! 断言避免 TS 在嵌套函数中反复判空
      const el = viewport!;
      el.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0');
      requestAnimationFrame(() => {
        el.setAttribute('content', originalContent);
      });
    }

    function handleResize() {
      // 如果可视宽度明显小于布局宽度，说明页面被放大了
      if (window.innerWidth < document.documentElement.scrollWidth - 1) {
        resetZoom();
      }
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', resetZoom);

    if ('visualViewport' in window) {
      const vv = (window as Window & { visualViewport?: EventTarget & { scale: number; addEventListener: (type: string, listener: EventListener) => void; removeEventListener: (type: string, listener: EventListener) => void } }).visualViewport;
      if (vv) {
        let lastScale = vv.scale || 1;
        const onVisualViewportChange = () => {
          const scale = vv.scale || 1;
          // 从放大状态回到 1 附近，说明键盘收起/缩放还原
          if (lastScale > 1.05 && scale <= 1.05) {
            resetZoom();
          }
          lastScale = scale;
        };
        vv.addEventListener('resize', onVisualViewportChange);
        return () => {
          window.removeEventListener('resize', handleResize);
          window.removeEventListener('orientationchange', resetZoom);
          vv.removeEventListener('resize', onVisualViewportChange);
        };
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', resetZoom);
    };
  }, []);
}
